import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import ConnectedModal from 'containers/ConnectedModal';
import useConnectedModal from 'containers/ConnectedModal/useConnectedModal';
import { createEmbedExportSelector } from 'containers/ProjectWizards/ExportVideoStep/utils';
import useGetProjectConfig from 'hooks/useGetProjectConfig';
import {
  makeProjectEmbedConfigurationUuidSelector,
  makeProjectWidgetUuidSelector,
} from 'redux/modules/entities';
import ProjectPreviewModalContents from './ProjectPreviewModalContents';
import { ProjectPreviewModalProvider } from './ProjectPreviewModalContext';
import useProjectRegeneration from './useProjectRegeneration';
import { block } from './utils';

export interface ProjectPreviewModalProps {}

const ProjectPreviewModal: React.FC<ProjectPreviewModalProps> = () => {
  const { show, params } = useConnectedModal('ProjectPreview');
  const { projectId } = params ?? {};

  const wid = useSelector(
    useMemo(() => makeProjectWidgetUuidSelector(projectId), [projectId]),
  );

  const embedConfigurationUuid = useSelector(
    useMemo(() => makeProjectEmbedConfigurationUuidSelector(projectId), [
      projectId,
    ]),
  );

  const {
    widgetId,
    embedConfig,
    projectLatestRegeneration,
    isUCSCompatible,
    fetchProjectConfig,
    reset,
  } = useGetProjectConfig({ projectId, widgetId: wid, embedConfigurationUuid });

  const { fileArchiveInfo = {}, id: embedVideoId } = useSelector(
    useMemo(() => createEmbedExportSelector(widgetId), [widgetId]),
  );

  useProjectRegeneration({ projectId, projectLatestRegeneration });

  useEffect(() => {
    if (!show || !projectId) {
      return undefined;
    }

    fetchProjectConfig();

    return () => reset();
  }, [fetchProjectConfig, projectId, reset, show]);

  return (
    <ConnectedModal v2 name="ProjectPreview" className={block()}>
      <ProjectPreviewModalProvider
        {...{
          projectId,
          widgetId,
          embedConfig,
          fileArchiveInfo,
          embedVideoId,
          projectLatestRegeneration,
          isUCSCompatible,
        }}
      >
        <ProjectPreviewModalContents
          onRegenerationFinished={fetchProjectConfig}
        />
      </ProjectPreviewModalProvider>
    </ConnectedModal>
  );
};

export default ProjectPreviewModal;
